import { Touchable } from "@kalyzee/kast-app-web-components";
import React, { useMemo, useRef, useState } from "react";
import { buildStyles, CircularProgressbar } from "react-circular-progressbar";
import { ReactComponent as IconCpu } from "../../../assets/icons/cpu.svg";
import { ReactComponent as IconDisk } from "../../../assets/icons/disk.svg";
import { ReactComponent as IconHealth } from "../../../assets/icons/health.svg";
import { ReactComponent as IconMemory } from "../../../assets/icons/memory.svg";
import Colors from "../../../constants/colors";
import { useOvenMediaAllStats, useOvenMediaContexts } from "../../../store/context/hooks";
import styles from "./system.module.css";

enum CardKey {
  HEALTH_SCORE = "health_score",
  CPU = "cpu",
  MEMORY = "memory",
  HOST_DISK = "host_disk",
  VIDEO_DISK = "video_disk",
}

type CardState = {
  [key in CardKey]: {
    show: boolean;
    enableAdditionalStats: boolean;
    opened: boolean;
  };
};

type CardMetaData = {
  [key in CardKey]: {
    icon: React.ReactNode;
    title: string;
  };
};

type CardData = {
  [key in CardKey]: {
    value: number;
    average: number;
    min?: number;
    max?: number;
    startedAt?: Date;
    last?: Date;
    percentage?: boolean;
  };
};

export interface DashboardSystemContainerProps {
  serverId?: string;
  className?: string;
  style?: React.CSSProperties;
}

const DashboardSystemContainer = ({ serverId, className, style }: DashboardSystemContainerProps) => {
  const now = new Date();
  const allStats = useOvenMediaAllStats();
  const contexts = useOvenMediaContexts();
  const { stats, context } = useMemo(() => {
    if (!serverId) return { stats: undefined, context: undefined };
    const context = contexts?.find((c) => c.server._id === serverId);
    if (!context) return { stats: undefined, context: undefined };
    return { stats: allStats?.[serverId], context };
  }, [serverId, allStats, contexts]);

  const cardDataRef = useRef<CardData>({
    [CardKey.HEALTH_SCORE]: { value: 0, average: 0, min: undefined, max: undefined, startedAt: now, last: now, percentage: false },
    [CardKey.CPU]: { value: 0, average: 0, min: undefined, max: undefined, startedAt: now, last: now, percentage: true },
    [CardKey.MEMORY]: { value: 0, average: 0, min: undefined, max: undefined, startedAt: now, last: now, percentage: true },
    [CardKey.HOST_DISK]: { value: 0, average: 0, min: undefined, max: undefined, startedAt: now, last: now, percentage: true },
    [CardKey.VIDEO_DISK]: { value: 0, average: 0, min: undefined, max: undefined, startedAt: now, last: now, percentage: true },
  });

  const [cardState, setCardState] = useState<CardState>({
    [CardKey.HEALTH_SCORE]: { show: true, enableAdditionalStats: true, opened: false },
    [CardKey.CPU]: { show: true, enableAdditionalStats: true, opened: false },
    [CardKey.MEMORY]: { show: serverId ? true : false, enableAdditionalStats: true, opened: false },
    [CardKey.HOST_DISK]: { show: serverId ? true : false, enableAdditionalStats: true, opened: false },
    [CardKey.VIDEO_DISK]: { show: serverId ? true : false, enableAdditionalStats: true, opened: false },
  });

  const cardMetadata: CardMetaData = {
    [CardKey.HEALTH_SCORE]: { icon: <IconHealth width={25} height={25} />, title: "HEALTH SCORE" /* TRANSLATION */ },
    [CardKey.CPU]: { icon: <IconCpu width={25} height={25} />, title: "CPU" /* TRANSLATION */ },
    [CardKey.MEMORY]: { icon: <IconMemory width={25} height={25} />, title: "MEMORY" /* TRANSLATION */ },
    [CardKey.HOST_DISK]: { icon: <IconDisk width={25} height={25} />, title: "HOST DISK" /* TRANSLATION */ },
    [CardKey.VIDEO_DISK]: { icon: <IconDisk width={25} height={25} />, title: "VIDEO DISK" /* TRANSLATION */ },
  };

  const updateCardData = (key: CardKey, value: number) => {
    cardDataRef.current[key].value = value;
    cardDataRef.current[key].min = cardDataRef.current[key].min ? Math.min(cardDataRef.current[key].min!, value) : value;
    cardDataRef.current[key].max = cardDataRef.current[key].max ? Math.max(cardDataRef.current[key].max!, value) : value;
    if (cardDataRef.current[key].last && cardDataRef.current[key].startedAt) {
      if (now.getTime() !== cardDataRef.current[key].startedAt!.getTime()) {
        cardDataRef.current[key].average =
          (cardDataRef.current[key].average * (cardDataRef.current[key].last!.getTime() - cardDataRef.current[key].startedAt!.getTime()) +
            value * (now.getTime() - cardDataRef.current[key].last!.getTime())) /
          (now.getTime() - cardDataRef.current[key].startedAt!.getTime());
      } else {
        cardDataRef.current[key].average = value;
      }
    } else {
      cardDataRef.current[key].average = value;
      cardDataRef.current[key].startedAt = now;
    }
    cardDataRef.current[key].last = now;
  };

  let healthScore = context?.healthScore ?? 0;
  let cpuUsage = (stats?.system?.cpuUsage ?? 0) * 100;
  if (!serverId) {
    healthScore = 0;
    cpuUsage = 0;
    if (contexts.length) {
      contexts.forEach((context) => {
        const serverId = context?.server?._id;
        if (!serverId) return;
        healthScore += context.healthScore ?? 0;
        const stats = allStats?.[serverId];
        cpuUsage += (stats?.system?.cpuUsage ?? 0) * 100;
      });
      healthScore = Math.round(healthScore/contexts.length * 10) / 10;
      cpuUsage = cpuUsage / contexts.length;
    }
  }
  const memoryUsage = (stats?.system?.memoryUsage ?? 0) * 100;
  const hostDiskUsage = (stats?.system?.hostDiskUsage ?? 0) * 100;
  const videoDiskUsage = (stats?.system?.videoDiskUsage ?? 0) * 100;

  updateCardData(CardKey.HEALTH_SCORE, healthScore);
  updateCardData(CardKey.CPU, cpuUsage);
  updateCardData(CardKey.MEMORY, memoryUsage);
  updateCardData(CardKey.HOST_DISK, hostDiskUsage);
  updateCardData(CardKey.VIDEO_DISK, videoDiskUsage);

  const formatPercentage = (value: number) => {
    return `${value?.toFixed(0)}%`;
  };
  const classes = [styles.container];
  if (className) classes.push(className);

  const getUsageColor = (value: number) => {
    if (value < 60) return Colors.getMainGreen();
    if (value < 80) return Colors.getTreePoppy();
    return Colors.getTorchRed();
  };

  const renderCard = (key: CardKey, formatValue?: (value: number) => { value?: number; text?: string }, getColor?: (value: number) => string) => {
    const data = cardDataRef.current[key];
    if (!cardState[key].show) return null;
    const color = getColor?.(data.value) ?? getUsageColor(data.value);
    const valueFormatted = formatValue?.(data.value);
    return (
      <div className={styles.detailsContainer}>
        <div className={styles.headerDetailsContainer}>
          {cardMetadata[key].icon}
          <div className={styles.detailsTitle}>{cardMetadata[key].title}</div>
        </div>
        <br />
        <div style={{ width: 100, height: 100 }}>
          <CircularProgressbar
            styles={buildStyles({
              pathColor: color,
              textColor: color,
            })}
            value={valueFormatted?.value ?? data.value}
            text={valueFormatted?.text ?? (data.percentage ? formatPercentage(data.value) : `${data.value}`)}
          />
        </div>
        {cardState[key].opened ? (
          <div style={{ width: "100%", marginBottom: 5 }}>
            <br />
            <div className={styles.detailsRow}>
              <span className={styles.detailsTitle}>{"Average:" /* TRANSLATION */}</span>
              <span className={styles.detailsValue}>{data.percentage ? formatPercentage(data.average) : `${data.value}`}</span>
            </div>
            <div className={styles.detailsRow}>
              <span className={styles.detailsTitle}>{"Min:" /* TRANSLATION */}</span>
              <span className={styles.detailsValue}>{data.percentage ? formatPercentage(data.min ?? 0) : `${data.min ?? 0}`}</span>
            </div>
            <div className={styles.detailsRow}>
              <span className={styles.detailsTitle}>{"Max:" /* TRANSLATION */}</span>
              <span className={styles.detailsValue}>{data.percentage ? formatPercentage(data.max ?? 0) : `${data.max ?? 0}`}</span>
            </div>
            <br />
            {data.startedAt ? (
              <div className={styles.startAt}>{`Started at: ${data.startedAt.toLocaleDateString()} ${data.startedAt.toLocaleTimeString()}`}</div>
            ) : null}
            <Touchable
              className={styles.resetButton}
              onPress={() => {
                cardDataRef.current[key].average = 0;
                cardDataRef.current[key].min = undefined;
                cardDataRef.current[key].max = undefined;
                cardDataRef.current[key].startedAt = undefined;
                cardDataRef.current[key].last = undefined;
                // Refresh now
                setCardState({ ...cardState });
              }}
            >
              {"Reset" /* TRANSLATION */}
            </Touchable>
          </div>
        ) : null}
        {cardState[key].enableAdditionalStats ? (
          <Touchable
            className={styles.openCloseContainer}
            onPress={() => {
              const newState = { ...cardState };
              newState[key].opened = !newState[key].opened;
              setCardState(newState);
            }}
          >
            {cardState[key].opened ? "▲" : "▼"}
          </Touchable>
        ) : null}
      </div>
    );
  };

  return (
    <div className={classes.join(" ")} style={style}>
      <div className={styles.rowContainer}>
        {renderCard(
          CardKey.HEALTH_SCORE,
          (value) => {
            return { value: value * 20 };
          },
          (value) => {
            if (value >= 5) return Colors.getMainGreen();
            if (value >= 4) return "rgb(205 229 102)";
            if (value >= 2) return Colors.getTreePoppy();
            return Colors.getTorchRed();
          }
        )}
        {renderCard(CardKey.CPU)}
        {renderCard(CardKey.MEMORY)}
        {renderCard(CardKey.HOST_DISK)}
        {renderCard(CardKey.VIDEO_DISK)}
      </div>
    </div>
  );
};

export default DashboardSystemContainer;
